import { useState, useRef, useEffect } from "react";
import { Link } from "wouter";
import { ToolContainer, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Mic, Square, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { getMediaErrorMessage } from "@/lib/mediaPermissions";
import { MediaPermissionAlert } from "@/components/MediaPermissionAlert";
import { loadFFmpeg, fetchFile, ffmpegOutputToBlob, resetFFmpegEngine } from "@/lib/ffmpegEngine";
import { convertAudioFormat, AUDIO_FORMAT_OPTIONS } from "./operations";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Records audio directly from your microphone and lets you download it, useful for quick voice memos, narration, or capturing an idea without a separate recording app. Runs entirely in your browser — nothing is uploaded anywhere.`,
  how: `Click Start Recording, grant microphone access, and speak. Pause and resume as needed, click Stop when you're done, then preview and download the recording.`,
  tip: `The level bar shows that your microphone is being heard; if it stays flat, check the microphone. Your browser records WebM or M4A. Use Download as MP3 if you need a file that plays everywhere (WhatsApp, email, older players).`,
};

// Checked in order of preference -- browsers vary in which of these they
// actually support, and MediaRecorder.isTypeSupported can report true for
// a generic type while still failing to actually record with it in some
// versions, so this tries several real candidates rather than just one.
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

function pickSupportedMimeType(): string[] {
  if (typeof MediaRecorder === "undefined") return [];
  return MIME_CANDIDATES.filter(candidate => MediaRecorder.isTypeSupported(candidate));
}

export default function VoiceRecorder() {
  const [recording, setRecording] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [mics, setMics] = useState<{ id: string; label: string }[]>([]);
  const [micId, setMicId] = useState("");
  const refreshMics = async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setMics(all.filter(d => d.kind === "audioinput" && d.deviceId && d.deviceId !== "default" && d.deviceId !== "communications").map((d, i) => ({ id: d.deviceId, label: d.label || `Microphone ${i + 1}` })));
    } catch { /* picker simply stays hidden */ }
  };
  useEffect(() => { if (typeof navigator !== "undefined" && "enumerateDevices" in (navigator.mediaDevices ?? {})) void refreshMics(); }, []);
  const [level, setLevel] = useState(0);
  const [quiet, setQuiet] = useState(false);
  const [mp3Busy, setMp3Busy] = useState(false);
  const pausedRef = useRef(false);
  const meterRef = useRef<{ ctx: AudioContext; raf: number } | null>(null);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [resultUrl, setResultUrl] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Live input level: shows the microphone is being heard, and warns if nothing comes in for ~3 s.
  const startMeter = (stream: MediaStream) => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioCtx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      let silentSince = performance.now();
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i] - 128) / 128);
        setLevel(peak);
        const now = performance.now();
        if (peak > 0.02 || pausedRef.current) silentSince = now;
        setQuiet(now - silentSince > 3000);
        meterRef.current!.raf = requestAnimationFrame(tick);
      };
      meterRef.current = { ctx, raf: requestAnimationFrame(tick) };
    } catch { /* the meter is optional */ }
  };
  const stopMeter = () => {
    if (meterRef.current) { cancelAnimationFrame(meterRef.current.raf); meterRef.current.ctx.close().catch(() => {}); meterRef.current = null; }
    setLevel(0); setQuiet(false);
  };

  // Recording lives only in this tab, so closing it would lose the take.
  useEffect(() => {
    if (!recording) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [recording]);
  useEffect(() => () => stopMeter(), []);

  const startRecording = async () => {
    setMicError(null);
    // Guards against an orphaned stream if this somehow gets triggered
    // while a previous one is still active (e.g. a rapid double-click).
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("This browser doesn't support recording from a webpage. Try a recent version of Chrome, Edge, Firefox, or Safari.");
      return;
    }

    let stream: MediaStream;
    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: micId ? { deviceId: { exact: micId } } : true });
      } catch (err) {
        // A microphone that was unplugged since it was chosen: use the default one instead of failing.
        if (micId && err instanceof DOMException && err.name === "OverconstrainedError") {
          setMicId("");
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } else throw err;
      }
      void refreshMics(); // names are only available once permission is granted
    } catch (err) {
      setMicError(getMediaErrorMessage(err, "microphone"));
      return;
    }

    const mimeCandidates = pickSupportedMimeType();
    if (mimeCandidates.length === 0) {
      stream.getTracks().forEach(t => t.stop());
      toast.error("This browser doesn't support any audio recording format this tool can use. Try a recent version of Chrome, Edge, or Firefox.");
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];
    setResultBlob(null);
    setResultUrl("");

    // isTypeSupported() can claim a format works and still fail at actual
    // construction in some browser versions (the exact gap this was
    // hitting) -- so this tries each supported candidate in turn instead
    // of giving up after the first one fails.
    let recorder: MediaRecorder | null = null;
    let usedMimeType = "";
    let lastConstructionError: unknown = null;
    for (const mimeType of mimeCandidates) {
      try {
        recorder = new MediaRecorder(stream, { mimeType });
        usedMimeType = mimeType;
        break;
      } catch (err) {
        lastConstructionError = err;
        console.warn(`MediaRecorder construction failed for ${mimeType}, trying next candidate:`, err);
      }
    }

    if (!recorder) {
      console.error("All MediaRecorder mime type candidates failed:", lastConstructionError);
      stream.getTracks().forEach(t => t.stop());
      toast.error("Couldn't start recording with this browser's audio format. Check the browser console (F12) for the specific error.");
      return;
    }

    try {
      const mimeType = usedMimeType;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setResultBlob(blob);
        setResultUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
        stopMeter();
      };
      recorder.onerror = (e) => {
        console.error("MediaRecorder error:", e);
        toast.error("Recording stopped unexpectedly. Check the browser console (F12) for the specific error.");
        stream.getTracks().forEach(t => t.stop());
        setRecording(false);
        stopMeter();
        if (timerRef.current) clearInterval(timerRef.current);
      };
      nameStamp.current = new Date();
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      pausedRef.current = false;
      setPaused(false);
      setRecording(true);
      setElapsed(0);
      startMeter(stream);
      timerRef.current = setInterval(() => { if (!pausedRef.current) setElapsed(e => e + 1); }, 1000);
    } catch (err) {
      // Isolates a recorder.start() failure (distinct from the
      // construction-cascade above) from the permission-request step,
      // since those need different messages to be useful.
      console.error("Failed to start MediaRecorder:", err);
      stream.getTracks().forEach(t => t.stop());
      toast.error("Couldn't start recording with this browser's audio format. Check the browser console (F12) for the specific error.");
    }
  };

  const togglePause = () => {
    const r = mediaRecorderRef.current;
    if (!r) return;
    if (pausedRef.current) { r.resume(); pausedRef.current = false; setPaused(false); }
    else { r.pause(); pausedRef.current = true; setPaused(true); }
  };

  const downloadMp3 = async () => {
    if (!resultBlob) return;
    setMp3Busy(true);
    let ffmpeg;
    try {
      ffmpeg = await loadFFmpeg(() => {});
      await ffmpeg.writeFile("rec.in", await fetchFile(resultBlob));
      const mp3 = AUDIO_FORMAT_OPTIONS.find(f => f.value === "mp3")!;
      const data = await convertAudioFormat(ffmpeg, "rec.in", "rec.mp3", mp3.codec, () => {});
      downloadBlob(ffmpegOutputToBlob(data, "audio/mpeg"), `${recordingName()}.mp3`);
    } catch (err) {
      console.error("MP3 conversion failed:", err);
      toast.error("Couldn't convert the recording to MP3. The original download still works, or try the Audio Converter.");
    } finally {
      if (ffmpeg) resetFFmpegEngine(ffmpeg);
      setMp3Busy(false);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    pausedRef.current = false;
    setPaused(false);
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const nameStamp = useRef(new Date());
  const recordingName = () => {
    const d = nameStamp.current, p2 = (n: number) => String(n).padStart(2, "0");
    return `voice-recording-${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}_${p2(d.getHours())}-${p2(d.getMinutes())}`;
  };

  const extensionFor = (blob: Blob) => {
    if (blob.type.includes("mp4")) return "m4a";
    if (blob.type.includes("ogg")) return "ogg";
    return "webm";
  };

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto text-center space-y-6">
        {!recording && !resultBlob && (
          <div className="py-12 border-2 border-dashed rounded-xl space-y-4">
            <Mic className="h-12 w-12 mx-auto text-muted-foreground" />
            {mics.length > 1 && (
              <label className="flex items-center justify-center gap-2 text-sm mb-3">Microphone
                <select className="border rounded px-2 py-1 bg-background max-w-[16rem]" value={micId} onChange={(e) => setMicId(e.target.value)} aria-label="Microphone">
                  <option value="">Default</option>
                  {mics.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </label>
            )}
            <Button onClick={startRecording} className="gap-2"><Mic className="h-4 w-4" /> Start Recording</Button>
            {micError && (
              <div className="px-4 text-left">
                <MediaPermissionAlert message={micError}>
                  <p className="mt-2">
                    No working microphone? You can still process audio you already have with the{" "}
                    <Link href="/tool/audio-converter" className="underline hover:text-amber-100">
                      Audio Converter
                    </Link>{" "}
                    or{" "}
                    <Link href="/tool/audio-trimmer" className="underline hover:text-amber-100">
                      Audio Trimmer
                    </Link>{" "}
                    -- both accept an uploaded file instead of recording live.
                  </p>
                </MediaPermissionAlert>
              </div>
            )}
          </div>
        )}

        {recording && (
          <div className="py-12 border-2 border-destructive/30 rounded-xl space-y-4 bg-destructive/5">
            <div className="flex items-center justify-center gap-2">
              <span className={`h-3 w-3 rounded-full bg-destructive ${paused ? "" : "animate-pulse"}`} />
              <p className="text-2xl font-mono font-bold" data-testid="rec-time">{formatTime(elapsed)}{paused ? " (paused)" : ""}</p>
            </div>
            <div className="mx-auto h-2 w-48 rounded-full bg-muted overflow-hidden" role="meter" aria-label="Microphone level" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(Math.min(1, level * 2) * 100)}>
              <div className="h-full bg-primary transition-[width] duration-75" style={{ width: `${Math.min(100, level * 200)}%` }} />
            </div>
            {quiet && !paused && <NoticeBox className="text-sm" testId="rec-quiet">We're not hearing anything. Check that the right microphone is selected and not muted.</NoticeBox>}
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={togglePause} className="gap-2">{paused ? <><Play className="h-4 w-4" /> Resume</> : <><Pause className="h-4 w-4" /> Pause</>}</Button>
              <Button variant="destructive" onClick={stopRecording} className="gap-2"><Square className="h-4 w-4" /> Stop Recording</Button>
            </div>
          </div>
        )}

        {resultBlob && !recording && (
          <div className="p-4 rounded-lg border bg-card space-y-3">
            <audio src={resultUrl} controls className="w-full" />
            <div className="flex gap-3 justify-center">
              <DownloadButton blob={resultBlob} fileName={`${recordingName()}.${extensionFor(resultBlob)}`} />
              <Button variant="outline" onClick={downloadMp3} disabled={mp3Busy}>{mp3Busy ? "Converting..." : "Download as MP3"}</Button>
              <Button variant="outline" onClick={() => { setResultBlob(null); setResultUrl(""); nameStamp.current = new Date(); }}>Record Again</Button>
            </div>
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
